import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  NativeSelect,
  Spinner,
  Switch,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ImageIcon,
  PencilIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  ShieldAlertIcon,
  TrashIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import {
  type CreateCustomerRequest,
  type CustomerSummary,
  customersApi,
  type UpdateCustomerRequest,
} from '~/lib/api'
import { toaster } from '~/toaster'

export const Route = createFileRoute('/_app/admin/customers')({
  component: CustomersPage,
})

const INDUSTRIES = [
  'Automotive',
  'Aviation',
  'Energy',
  'Healthcare',
  'Industrial',
  'Logistics',
  'Marketing',
  'Pharma',
  'Smart Home',
  'Telecare',
  'Traffic',
  'Utilities',
  'Fleet Management',
  'Access Control',
]

function CreateCustomerModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void
  onSuccess: () => void
}) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [industry, setIndustry] = useState('Automotive')
  const [description, setDescription] = useState('')
  const [challenge, setChallenge] = useState('')
  const [solution, setSolution] = useState('')
  const [resultsText, setResultsText] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [color, setColor] = useState('#3B82F6')
  const [displayOnHome, setDisplayOnHome] = useState(true)
  const [displayOnMap, setDisplayOnMap] = useState(true)

  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerRequest) => customersApi.create(data),
    onSuccess: () => {
      toaster.create({
        title: 'Customer created',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create customer',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!slug || !name || !industry) {
      toaster.create({
        title: 'Missing required fields',
        description: 'Slug, name, and industry are required',
        type: 'error',
      })
      return
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    const results = resultsText
      .split('\n')
      .map((r) => r.trim())
      .filter((r) => r.length > 0)

    createMutation.mutate({
      slug,
      name,
      industry,
      description: description || undefined,
      challenge: challenge || undefined,
      solution: solution || undefined,
      results: results.length > 0 ? results : undefined,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      color,
      displayOnHome,
      displayOnMap,
    })
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="700px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Add New Customer</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6} maxH="70vh" overflowY="auto">
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Slug *
                  </Text>
                  <Input
                    placeholder="bmw"
                    value={slug}
                    onChange={(e) =>
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                    }
                    required
                  />
                  <Text fontSize="xs" color="content.secondary" mt={1}>
                    Used in URL: /customers/{slug || 'slug'}
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Name *
                  </Text>
                  <Input
                    placeholder="BMW"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Box>
              </HStack>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Industry *
                  </Text>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                    >
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>
                          {ind}
                        </option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Color
                  </Text>
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    h="40px"
                  />
                </Box>
              </HStack>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Description
                </Text>
                <Textarea
                  placeholder="Brief description of the customer..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Challenge
                </Text>
                <Textarea
                  placeholder="What challenges did they face?"
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Solution
                </Text>
                <Textarea
                  placeholder="How did HiveMQ help?"
                  value={solution}
                  onChange={(e) => setSolution(e.target.value)}
                  rows={2}
                />
              </Box>

              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Results (one per line)
                </Text>
                <Textarea
                  placeholder="Result 1&#10;Result 2&#10;Result 3"
                  value={resultsText}
                  onChange={(e) => setResultsText(e.target.value)}
                  rows={3}
                />
              </Box>

              <HStack gap={4}>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Latitude
                  </Text>
                  <Input
                    type="number"
                    step="any"
                    placeholder="48.1351"
                    value={latitude}
                    onChange={(e) => setLatitude(e.target.value)}
                  />
                </Box>
                <Box flex={1}>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Longitude
                  </Text>
                  <Input
                    type="number"
                    step="any"
                    placeholder="11.5820"
                    value={longitude}
                    onChange={(e) => setLongitude(e.target.value)}
                  />
                </Box>
              </HStack>

              <HStack gap={6}>
                <HStack gap={2}>
                  <Switch.Root
                    checked={displayOnHome}
                    onCheckedChange={(e) => setDisplayOnHome(e.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                  <Text fontSize="sm">Show on Home</Text>
                </HStack>
                <HStack gap={2}>
                  <Switch.Root
                    checked={displayOnMap}
                    onCheckedChange={(e) => setDisplayOnMap(e.checked)}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control />
                  </Switch.Root>
                  <Text fontSize="sm">Show on Map</Text>
                </HStack>
              </HStack>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="blue" loading={createMutation.isPending}>
                  Create Customer
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

function EditCustomerModal({
  customer,
  onClose,
  onSuccess,
}: {
  customer: CustomerSummary
  onClose: () => void
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)

  // Fetch full customer details
  const { data: customerDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['customer', customer.id],
    queryFn: () => customersApi.get(customer.id),
  })

  const [slug, setSlug] = useState(customer.slug)
  const [name, setName] = useState(customer.name)
  const [industry, setIndustry] = useState(customer.industry)
  const [description, setDescription] = useState(customer.description || '')
  const [challenge, setChallenge] = useState('')
  const [solution, setSolution] = useState('')
  const [resultsText, setResultsText] = useState('')
  const [latitude, setLatitude] = useState(customer.latitude?.toString() || '')
  const [longitude, setLongitude] = useState(customer.longitude?.toString() || '')
  const [color, setColor] = useState(customer.color)
  const [isActive, setIsActive] = useState(customer.isActive)
  const [displayOnHome, setDisplayOnHome] = useState(customer.displayOnHome)
  const [displayOnMap, setDisplayOnMap] = useState(customer.displayOnMap)

  // Update state when detail loads
  useState(() => {
    if (customerDetail) {
      setChallenge(customerDetail.challenge || '')
      setSolution(customerDetail.solution || '')
      setResultsText(customerDetail.results?.join('\n') || '')
    }
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCustomerRequest) => customersApi.update(customer.id, data),
    onSuccess: () => {
      toaster.create({
        title: 'Customer updated',
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update customer',
        description: error.message,
        type: 'error',
      })
    },
  })

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => customersApi.uploadLogo(customer.id, file),
    onSuccess: () => {
      toaster.create({
        title: 'Logo uploaded',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to upload logo',
        description: error.message,
        type: 'error',
      })
    },
  })

  const uploadCoverMutation = useMutation({
    mutationFn: (file: File) => customersApi.uploadCover(customer.id, file),
    onSuccess: () => {
      toaster.create({
        title: 'Cover uploaded',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['customer', customer.id] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to upload cover',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validate slug format
    if (slug && !/^[a-z0-9-]+$/.test(slug)) {
      toaster.create({
        title: 'Invalid slug',
        description: 'Slug must contain only lowercase letters, numbers, and hyphens',
        type: 'error',
      })
      return
    }

    const results = resultsText
      .split('\n')
      .map((r) => r.trim())
      .filter((r) => r.length > 0)

    updateMutation.mutate({
      slug: slug !== customer.slug ? slug : undefined,
      name: name !== customer.name ? name : undefined,
      industry: industry !== customer.industry ? industry : undefined,
      description: description || undefined,
      challenge: challenge || undefined,
      solution: solution || undefined,
      results: results.length > 0 ? results : undefined,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      color: color !== customer.color ? color : undefined,
      isActive: isActive !== customer.isActive ? isActive : undefined,
      displayOnHome: displayOnHome !== customer.displayOnHome ? displayOnHome : undefined,
      displayOnMap: displayOnMap !== customer.displayOnMap ? displayOnMap : undefined,
    })
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadLogoMutation.mutate(file)
    }
  }

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadCoverMutation.mutate(file)
    }
  }

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Heading size="md">Edit Customer: {customer.name}</Heading>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6} maxH="70vh" overflowY="auto">
          {isLoadingDetail ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : (
            <form onSubmit={handleSubmit}>
              <VStack gap={4} align="stretch">
                {/* Image uploads */}
                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={2} fontSize="sm">
                      Logo
                    </Text>
                    <Box
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderRadius="lg"
                      p={4}
                      textAlign="center"
                      cursor="pointer"
                      onClick={() => logoInputRef.current?.click()}
                      _hover={{ borderColor: 'blue.500' }}
                    >
                      {customer.hasLogo ? (
                        <Image
                          src={customersApi.getLogoUrl(customer.id)}
                          alt="Logo"
                          maxH="60px"
                          mx="auto"
                          objectFit="contain"
                        />
                      ) : (
                        <VStack gap={1}>
                          <ImageIcon size={24} />
                          <Text fontSize="sm" color="content.secondary">
                            Click to upload logo
                          </Text>
                        </VStack>
                      )}
                    </Box>
                    <input
                      type="file"
                      ref={logoInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Box>
                  <Box flex={2}>
                    <Text fontWeight="medium" mb={2} fontSize="sm">
                      Cover Image
                    </Text>
                    <Box
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderRadius="lg"
                      p={4}
                      textAlign="center"
                      cursor="pointer"
                      onClick={() => coverInputRef.current?.click()}
                      _hover={{ borderColor: 'blue.500' }}
                    >
                      {customer.hasCover ? (
                        <Image
                          src={customersApi.getCoverUrl(customer.id)}
                          alt="Cover"
                          maxH="80px"
                          mx="auto"
                          objectFit="cover"
                        />
                      ) : (
                        <VStack gap={1}>
                          <UploadIcon size={24} />
                          <Text fontSize="sm" color="content.secondary">
                            Click to upload cover
                          </Text>
                        </VStack>
                      )}
                    </Box>
                    <input
                      type="file"
                      ref={coverInputRef}
                      style={{ display: 'none' }}
                      accept="image/*"
                      onChange={handleCoverUpload}
                    />
                  </Box>
                </HStack>

                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Slug *
                    </Text>
                    <Input
                      placeholder="bmw"
                      value={slug}
                      onChange={(e) =>
                        setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                      }
                      required
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Name *
                    </Text>
                    <Input
                      placeholder="BMW"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </Box>
                </HStack>

                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Industry *
                    </Text>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                      >
                        {INDUSTRIES.map((ind) => (
                          <option key={ind} value={ind}>
                            {ind}
                          </option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Color
                    </Text>
                    <Input
                      type="color"
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      h="40px"
                    />
                  </Box>
                </HStack>

                <Box>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Description
                  </Text>
                  <Textarea
                    placeholder="Brief description of the customer..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                  />
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Challenge
                  </Text>
                  <Textarea
                    placeholder="What challenges did they face?"
                    value={challenge || customerDetail?.challenge || ''}
                    onChange={(e) => setChallenge(e.target.value)}
                    rows={2}
                  />
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Solution
                  </Text>
                  <Textarea
                    placeholder="How did HiveMQ help?"
                    value={solution || customerDetail?.solution || ''}
                    onChange={(e) => setSolution(e.target.value)}
                    rows={2}
                  />
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={1} fontSize="sm">
                    Results (one per line)
                  </Text>
                  <Textarea
                    placeholder="Result 1&#10;Result 2&#10;Result 3"
                    value={resultsText || customerDetail?.results?.join('\n') || ''}
                    onChange={(e) => setResultsText(e.target.value)}
                    rows={3}
                  />
                </Box>

                <HStack gap={4}>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Latitude
                    </Text>
                    <Input
                      type="number"
                      step="any"
                      placeholder="48.1351"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text fontWeight="medium" mb={1} fontSize="sm">
                      Longitude
                    </Text>
                    <Input
                      type="number"
                      step="any"
                      placeholder="11.5820"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                    />
                  </Box>
                </HStack>

                <HStack gap={6}>
                  <HStack gap={2}>
                    <Switch.Root checked={isActive} onCheckedChange={(e) => setIsActive(e.checked)}>
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                    <Text fontSize="sm">Active</Text>
                  </HStack>
                  <HStack gap={2}>
                    <Switch.Root
                      checked={displayOnHome}
                      onCheckedChange={(e) => setDisplayOnHome(e.checked)}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                    <Text fontSize="sm">Show on Home</Text>
                  </HStack>
                  <HStack gap={2}>
                    <Switch.Root
                      checked={displayOnMap}
                      onCheckedChange={(e) => setDisplayOnMap(e.checked)}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                    <Text fontSize="sm">Show on Map</Text>
                  </HStack>
                </HStack>

                <HStack gap={2} justify="flex-end" pt={4}>
                  <Button variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" colorPalette="blue" loading={updateMutation.isPending}>
                    Save Changes
                  </Button>
                </HStack>
              </VStack>
            </form>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function CustomersPage() {
  const { hasMinRole } = useAuth()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<CustomerSummary | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [industryFilter, setIndustryFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // Check if user has at least operator role
  const isOperatorOrAbove = hasMinRole('operator')

  // Redirect if not operator or above
  if (!isOperatorOrAbove) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="danger.subtle">
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="danger.fg">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>operator</strong> role or higher.
          </Text>
        </VStack>
      </Flex>
    )
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'customers',
      { search: searchQuery, industry: industryFilter, page: currentPage, pageSize },
    ],
    queryFn: () =>
      customersApi.list({
        page: currentPage,
        pageSize,
        activeOnly: false,
        search: searchQuery || undefined,
        industry: industryFilter || undefined,
      }),
    retry: false,
  })

  // Reset to first page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const handleIndustryChange = (value: string) => {
    setIndustryFilter(value)
    setCurrentPage(1)
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customersApi.delete(id),
    onSuccess: () => {
      toaster.create({
        title: 'Customer deleted',
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete customer',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (customer: CustomerSummary) => {
    if (confirm(`Are you sure you want to delete "${customer.name}"?`)) {
      deleteMutation.mutate(customer.id)
    }
  }

  const customers = data?.customers || []

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        title="Customer Management"
        subtitle="Manage customer use cases and case studies"
        actions={
          <>
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Add Customer
            </Button>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        {/* Filters */}
        <HStack gap={4}>
          <Box flex={1} maxW="300px">
            <HStack>
              <SearchIcon size={16} />
              <Input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="sm"
              />
            </HStack>
          </Box>
          <Box>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={industryFilter}
                onChange={(e) => handleIndustryChange(e.target.value)}
                placeholder="All Industries"
              >
                <option value="">All Industries</option>
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Box>
        </HStack>

        <Card.Root>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="60px">Logo</Table.ColumnHeader>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>Industry</Table.ColumnHeader>
                <Table.ColumnHeader>Location</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Visibility</Table.ColumnHeader>
                <Table.ColumnHeader w="100px">Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : customers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={7}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No customers found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                customers.map((customer) => (
                  <Table.Row key={customer.id}>
                    <Table.Cell>
                      {customer.hasLogo ? (
                        <Image
                          src={customersApi.getLogoUrl(customer.id)}
                          alt={customer.name}
                          w="40px"
                          h="40px"
                          objectFit="contain"
                          borderRadius="md"
                        />
                      ) : (
                        <Box
                          w="40px"
                          h="40px"
                          bg="bg.subtle"
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <ImageIcon size={20} color="var(--chakra-colors-gray-400)" />
                        </Box>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <VStack align="flex-start" gap={0}>
                        <Text fontWeight="medium">{customer.name}</Text>
                        <Text fontSize="sm" color="content.secondary">
                          /{customer.slug}
                        </Text>
                      </VStack>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={2}>
                        <Box w="12px" h="12px" bg={customer.color} borderRadius="full" />
                        <Text fontSize="sm">{customer.industry}</Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      {customer.latitude && customer.longitude ? (
                        <Text fontSize="sm">
                          {customer.latitude.toFixed(2)}, {customer.longitude.toFixed(2)}
                        </Text>
                      ) : (
                        <Text fontSize="sm" color="content.tertiary">
                          Not set
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={customer.isActive ? 'green' : 'red'} variant="subtle">
                        {customer.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <Badge
                          colorPalette={customer.displayOnHome ? 'green' : 'gray'}
                          variant="subtle"
                        >
                          {customer.displayOnHome ? 'Home' : '-'}
                        </Badge>
                        <Badge
                          colorPalette={customer.displayOnMap ? 'green' : 'gray'}
                          variant="subtle"
                        >
                          {customer.displayOnMap ? 'Map' : '-'}
                        </Badge>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingCustomer(customer)}
                          aria-label={`Edit ${customer.name}`}
                        >
                          <PencilIcon size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => handleDelete(customer)}
                          aria-label={`Delete ${customer.name}`}
                        >
                          <TrashIcon size={14} />
                        </Button>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {data && data.total > 0 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="content.secondary">
              Showing {(currentPage - 1) * pageSize + 1} -{' '}
              {Math.min(currentPage * pageSize, data.total)} of {data.total} customers
            </Text>
            <HStack gap={1}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeftIcon size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon size={16} />
              </Button>
              <HStack gap={1} px={2}>
                {Array.from({ length: Math.ceil(data.total / pageSize) }, (_, i) => i + 1)
                  .filter((page) => {
                    const totalPages = Math.ceil(data.total / pageSize)
                    if (totalPages <= 7) return true
                    if (page === 1 || page === totalPages) return true
                    if (Math.abs(page - currentPage) <= 1) return true
                    return false
                  })
                  .reduce<(number | 'ellipsis')[]>((acc, page, idx, arr) => {
                    if (idx > 0 && page - (arr[idx - 1] as number) > 1) {
                      acc.push('ellipsis')
                    }
                    acc.push(page)
                    return acc
                  }, [])
                  .map((item, idx) =>
                    item === 'ellipsis' ? (
                      <Text key={`ellipsis-${idx}`} px={2} color="content.tertiary">
                        ...
                      </Text>
                    ) : (
                      <Button
                        key={item}
                        size="sm"
                        variant={currentPage === item ? 'solid' : 'outline'}
                        colorPalette={currentPage === item ? 'blue' : undefined}
                        onClick={() => setCurrentPage(item)}
                        minW="36px"
                      >
                        {item}
                      </Button>
                    ),
                  )}
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setCurrentPage((p) => Math.min(Math.ceil(data.total / pageSize), p + 1))
                }
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
              >
                <ChevronRightIcon size={16} />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.ceil(data.total / pageSize))}
                disabled={currentPage >= Math.ceil(data.total / pageSize)}
              >
                <ChevronsRightIcon size={16} />
              </Button>
            </HStack>
          </Flex>
        )}
      </VStack>

      {showCreateModal && (
        <CreateCustomerModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['customers'] })}
        />
      )}

      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['customers'] })}
        />
      )}
    </Box>
  )
}
